import { useState, useEffect } from 'react';
import { Shield } from 'lucide-react';
import { CalculatorCard, SliderInput } from '../CalculatorCard';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

export const InsuranceCalculator = () => {
  const [monthlyIncome, setMonthlyIncome] = useState(50000);
  const [yearsToReplace, setYearsToReplace] = useState(10);
  const [dependents, setDependents] = useState(2);
  const [existingDebts, setExistingDebts] = useState(500000);
  
  const [recommendedCoverage, setRecommendedCoverage] = useState(0);
  const [breakdown, setBreakdown] = useState([]);

  useEffect(() => {
    const incomeReplacement = monthlyIncome * 12 * yearsToReplace;
    const childCosts = 100000 * dependents * 10;
    const emergencyFund = monthlyIncome * 6;
    
    const total = incomeReplacement + existingDebts + childCosts + emergencyFund;
    setRecommendedCoverage(Math.round(total));

    setBreakdown([
      { name: 'Náhrada příjmu', value: incomeReplacement, color: '#22C55E' },
      { name: 'Splacení dluhů', value: existingDebts, color: '#EF4444' },
      { name: 'Náklady na děti', value: childCosts, color: '#3B82F6' },
      { name: 'Nouzová rezerva', value: emergencyFund, color: '#EAB308' },
    ]);
  }, [monthlyIncome, yearsToReplace, dependents, existingDebts]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-navy border border-white/10 rounded-lg p-3 shadow-lg">
          <p className="text-white font-semibold">{data.name}</p>
          <p className="text-green font-bold">{data.value.toLocaleString('cs-CZ')} Kč</p>
        </div>
      );
    }
    return null;
  };

  const result = (
    <div className="space-y-6">
      <div className="text-right">
        <p className="text-gray-400 text-sm">Doporučené životní pojištění</p>
        <p className="text-4xl font-bold text-green">{recommendedCoverage.toLocaleString('cs-CZ')} Kč</p>
      </div>

      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={breakdown} layout="vertical">
            <XAxis 
              type="number"
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
              tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
            />
            <YAxis 
              type="category"
              dataKey="name"
              tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={100}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" radius={[0, 6, 6, 0]}>
              {breakdown.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-green/10 rounded-xl p-4">
        <p className="text-gray-300 text-sm">
          <span className="font-semibold text-green">Pravidlo:</span> Pojistěte se na 10-12× roční příjem + dluhy
        </p>
      </div>
    </div>
  );

  return (
    <CalculatorCard
      id="pojisteni"
      title="Pojistná kalkulačka"
      subtitle="Doporučené krytí pro vaši životní situaci"
      icon={Shield}
      tip="Nejčastější chyba: nízké pojištění. Pokud máte hypotéku 3M Kč a pojistku jen na 500k Kč, rodina přijde o dům. Pojistěte se tak, aby rodina zvládla váš výpadek bez finančních problémů."
      result={result}
      showResult={true}
      testId="insurance-calculator"
    >
      <SliderInput
        label="Čistý měsíční příjem"
        value={monthlyIncome}
        onChange={setMonthlyIncome}
        min={15000}
        max={200000}
        step={1000}
        suffix="Kč"
        testId="insurance-income-slider"
      />
      <SliderInput
        label="Kolik let nahradit příjem"
        value={yearsToReplace}
        onChange={setYearsToReplace}
        min={3}
        max={20}
        step={1}
        suffix="let"
        testId="insurance-years-slider"
      />
      <SliderInput
        label="Počet závislých osob"
        value={dependents}
        onChange={setDependents}
        min={0}
        max={5}
        step={1}
        suffix=""
        formatValue={(v) => `${v} ${v === 1 ? 'osoba' : v > 1 && v < 5 ? 'osoby' : 'osob'}`}
        testId="insurance-dependents-slider"
      />
      <SliderInput
        label="Celkové dluhy"
        value={existingDebts}
        onChange={setExistingDebts}
        min={0}
        max={10000000}
        step={100000}
        suffix="Kč"
        testId="insurance-debts-slider"
      />
    </CalculatorCard>
  );
};
